import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const PROBE_TIMEOUT_MS = 4000;

@Injectable({
  providedIn: 'root',
})
export class NetworkConnectivityService {
  private readonly _isOnline = new BehaviorSubject<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  readonly isOnline$ = this._isOnline.asObservable();
  private probePromise: Promise<boolean> | null = null;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', () => {
      void this.refreshStatus();
    });
    window.addEventListener('offline', () => {
      this.setOffline();
    });
    window.addEventListener('focus', () => {
      void this.refreshStatus();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.refreshStatus();
      }
    });

    void this.refreshStatus();
  }

  get isOnline(): boolean {
    return this._isOnline.value;
  }

  async refreshOnlineStatus(): Promise<boolean> {
    await this.refreshStatus();
    return this.isOnline;
  }

  private setOffline(): void {
    if (this._isOnline.value !== false) {
      this._isOnline.next(false);
    }
  }

  private setOnline(): void {
    if (this._isOnline.value !== true) {
      this._isOnline.next(true);
    }
  }

  private isBrowserOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine;
  }

  private async refreshStatus(): Promise<void> {
    if (!this.isBrowserOnline()) {
      this.setOffline();
      return;
    }

    const online = await this.probeConnectivity();
    if (online) {
      this.setOnline();
    } else {
      this.setOffline();
    }
  }

  private probeConnectivity(): Promise<boolean> {
    if (this.probePromise) {
      return this.probePromise;
    }

    this.probePromise = this.runConnectivityProbe().finally(() => {
      this.probePromise = null;
    });

    return this.probePromise;
  }

  private buildProbeUrl(): string {
    const origin = window.location.origin;
    return `${origin}/index.html?ngsw-bypass=true&_=${Date.now()}`;
  }

  private async runConnectivityProbe(): Promise<boolean> {
    if (typeof fetch === 'undefined' || typeof window === 'undefined') {
      return this.isBrowserOnline();
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    try {
      await fetch(this.buildProbeUrl(), {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
